import React, { useState, useEffect } from 'react'
import { withStyles } from '@material-ui/core/styles'
import {
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import { CoreButton, CoreTextField } from 'src/components/core'
import styles from './styles'
import axios from 'axios'
import { useSelector } from 'react-redux'

const ITEMS_PER_PAGE = 20

const CommunityBoardModal = props => {
  const { classes, onConfirm, onCancel } = props
  const customDomain = useSelector(state => state.domain.list)
  const customDomainList = customDomain && customDomain.results
  const [selectedOption, setSelectedOption] = useState('create')
  const [boardName, setBoardName] = useState('')
  const [existingBoards, setExistingBoards] = useState([])
  const [selectedBoardId, setSelectedBoardId] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingBoards, setIsLoadingBoards] = useState(false)
  const [error, setError] = useState('')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Fetch existing boards when "existing" option is selected or page changes
  useEffect(() => {
    if (selectedOption === 'existing') {
      fetchExistingBoards(currentPage)
    }
  }, [selectedOption, currentPage])

  const fetchExistingBoards = async (page = 1) => {
    setIsLoadingBoards(true)
    setError('')

    try {
      const userId = window.CONSTANTS?.ID
      if (!userId) {
        setError('User ID not found')
        setIsLoadingBoards(false)
        return
      }

      const apiUrl = process.env.REACT_APP_COMMUNITY_API_URL || 'https://community-2zj0.onrender.com/api'
      const baseURL = apiUrl.replace('/api', '')

      const axiosInstance = axios.create({
        baseURL: baseURL,
        withCredentials: false,
      })

      const offset = (page - 1) * ITEMS_PER_PAGE
      const response = await axiosInstance.get(
        `/api/communities/user/${userId}`,
        {
          params: {
            limit: ITEMS_PER_PAGE,
            offset: offset,
          },
        }
      )

      const boards = response.data || []
      setExistingBoards(boards)

      // Calculate total pages based on results
      // If we got fewer items than requested, we're on the last page
      if (boards.length < ITEMS_PER_PAGE) {
        setTotalPages(page)
      } else {
        // There might be more pages, set at least current + 1
        setTotalPages(Math.max(totalPages, page + 1))
      }
    } catch (err) {
      console.error('Failed to fetch communities:', err)
      setError('Failed to load existing boards')
    } finally {
      setIsLoadingBoards(false)
    }
  }

  const handlePageChange = (event, value) => {
    if (value > 0 && value <= totalPages) {
      setCurrentPage(value)
      setSelectedBoardId(null)
    }
  }

  const handleOptionChange = event => {
    setSelectedOption(event.target.value)
    setError('')
    setSelectedBoardId(null)
    setCurrentPage(1)
    setTotalPages(1)
  }

  const handleBoardNameChange = e => {
    setBoardName(e.target.value)
    setError('')
  }

  const handleBoardSelect = boardId => {
    setSelectedBoardId(boardId)
    setError('')
    setIsDropdownOpen(false)
  }

  const handleCreate = async () => {
    if (selectedOption === 'create' && boardName.trim()) {
      setIsLoading(true)
      setError('')

      try {
        // URL'den ve Redux'tan bilgileri parse et
        const urlPath = window.location.pathname
        const isMiniCourse = urlPath.includes('/mini-course/')
        const isCollection = urlPath.includes('/collection/')
        const isLandingPage = urlPath.includes('/landing-page')

        let courseId = null
        let collectionId = null
        let pageId = null

        if (isMiniCourse) {
          const courseIdMatch = urlPath.match(/\/mini-course\/(\d+)/)
          const pageNumberMatch = urlPath.match(/\/page\/(\d+)/)
          courseId =
            window.CONSTANTS?.COURSE_ID ||
            (courseIdMatch ? courseIdMatch[1] : null)
          pageId = pageNumberMatch ? pageNumberMatch[1] : null
        } else if (isCollection) {
          const collectionIdMatch = urlPath.match(/\/collection\/(\d+)/)
          collectionId = collectionIdMatch ? collectionIdMatch[1] : null
          if (isLandingPage) {
            pageId = 'landing-page'
          }
        }

        // Redux store'dan course/collection bilgisini al
        let courseCode = null
        let pageNumber = null
        let pageIndex = null
        let orderOfCard = null
        let sections = null

        try {
          if (window.store && window.store.getState) {
            const state = window.store.getState()

            if (isMiniCourse) {
              const course = state?.course?.course
              courseCode = course?.code
              sections = course?.sections
            } else if (isCollection) {
              const collection = state?.collection?.collection
              courseCode = collection?.code
            }

            // Card'ın orderOfCard değerini ve section index'ini bul (sadece mini-course için)
            if (
              isMiniCourse &&
              sections &&
              Array.isArray(sections) &&
              pageId
            ) {
              for (let i = 0; i < sections.length; i++) {
                const section = sections[i]
                if (section.cards && Array.isArray(section.cards)) {
                  const card = section.cards.find(card => card.id == pageId)
                  if (card && card.orderOfCard !== undefined) {
                    pageIndex = i
                    orderOfCard = card.orderOfCard
                    pageNumber = card.orderOfCard + 1
                    break
                  }
                }
              }
            }
          }
        } catch (e) {
          console.log('Redux store error:', e)
        }

        // Fallback: window.CONSTANTS'tan dene
        if (!courseCode) {
          courseCode = window.CONSTANTS?.COURSE_CODE || window.CONSTANTS?.CODE
        }

        // Custom domain veya default domain'i belirle
        let domainName = 'share.minicoursegenerator.com'
        let shareLink = null

        if (customDomainList) {
          if (isCollection && collectionId) {
            // Collection için custom domain kontrolü
            const collectionDomain = customDomainList.filter(
              item =>
                item.defaultCollectionDto != null &&
                item.defaultCollectionDto.id == collectionId &&
                item.verificationStatus == 1,
            )
            if (collectionDomain.length > 0) {
              domainName = collectionDomain[0].domain
            }
          } else if (isMiniCourse && courseId) {
            // Mini-course için custom domain kontrolü
            const courseDomain = customDomainList.filter(
              item =>
                item.defaultCourseDto != null &&
                item.defaultCourseDto.id == courseId &&
                item.verificationStatus == 1,
            )
            if (courseDomain.length > 0) {
              domainName = courseDomain[0].domain
            }
          }
        }

        // Share link oluştur
        if (domainName !== 'share.minicoursegenerator.com') {
          // Custom domain kullanılıyorsa
          shareLink = `https://${domainName}`
        } else if (isCollection) {
          // Collection için default domain
          try {
            if (window.store && window.store.getState) {
              const state = window.store.getState()
              const collection = state?.collection?.collection
              if (collection) {
                const formattedName = collection.name
                  .replaceAll(' ', '-')
                  .replaceAll('?', '')
                  .replaceAll('&', '')
                  .replaceAll('#', '')
                  .replaceAll('%', '')
                shareLink = `https://${domainName}/category/${formattedName}-1/${collection.id}`
              }
            }
          } catch (e) {
            console.log('Collection share link error:', e)
          }
        } else if (isMiniCourse && courseCode) {
          // Mini-course için default domain
          shareLink = `https://${domainName}/${courseCode}`
        }

        // Token göndermeden API'ye POST isteği gönder
        // Yeni axios instance oluştur (interceptor'lar olmadan)
        const apiUrl = process.env.REACT_APP_COMMUNITY_API_URL || 'https://community-2zj0.onrender.com/api'
        const baseURL = apiUrl.replace('/api', '')

        const axiosInstance = axios.create({
          baseURL: baseURL,
          withCredentials: false, // CORS için credentials göndermiyoruz
        })

        const response = await axiosInstance.post('/api/communities/', {
          name: boardName.trim(),
          background_color: '#ffffff',
          icon: '',
          user_info: {
            userId: window.CONSTANTS?.ID,
            email: window.CONSTANTS?.EMAIL,
            name: window.CONSTANTS?.NAME,
            surname: window.CONSTANTS?.SUR_NAME,
            companyName: window.CONSTANTS?.COMPANY_NAME,
            url: [
              {
                adminUrl: window.location.href,
                shareLink: shareLink,
              },
            ],
          },
        })

        // Başarılı yanıt gelirse onConfirm'e board data'yı gönder
        const boardData = {
          type: 'create',
          boardId: response.data.id,
          name: response.data.name,
          backgroundColor: response.data.background_color,
          icon: response.data.icon,
          ...response.data,
        }

        onConfirm(boardData)
      } catch (err) {
        console.error('Community board creation error:', err)
        console.error('Error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          headers: err.response?.headers,
        })

        let errorMessage = 'Failed to create community board. Please try again.'

        if (err.response?.status === 403) {
          errorMessage =
            'Access forbidden. Please check CORS settings on the backend.'
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message
        }

        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    } else if (selectedOption === 'existing' && selectedBoardId) {
      // Handle existing board selection - no API call needed, just confirm
      const selectedBoard = existingBoards.find(
        board => board.id === selectedBoardId,
      )

      if (selectedBoard) {
        onConfirm({
          type: 'existing',
          boardId: selectedBoard.id,
          name: selectedBoard.name,
          backgroundColor: selectedBoard.background_color,
          icon: selectedBoard.icon,
          ...selectedBoard,
        })
      }
    }
  }

  const isCreateDisabled =
    (selectedOption === 'create' && !boardName.trim()) ||
    (selectedOption === 'existing' && !selectedBoardId)

  return (
    <Grid container className={classes.root}>
      <div className={classes.content}>
        <RadioGroup
          value={selectedOption}
          onChange={handleOptionChange}
          className={classes.radioGroup}
        >
          <FormControlLabel
            value="create"
            control={<Radio color="primary" />}
            label={
              <div className={classes.optionContainer}>
                <span className={classes.optionLabel}>Create a new board</span>
              </div>
            }
            className={classes.radioOption}
          />

          {selectedOption === 'create' && (
            <div className={classes.inputContainer}>
              <CoreTextField
                value={boardName}
                onChange={handleBoardNameChange}
                size="small"
                placeholder="Enter a name for your board"
                fullWidth
                autoFocus
                marginTop={false}
              />
            </div>
          )}

          <FormControlLabel
            value="existing"
            control={<Radio color="primary" />}
            label={
              <div className={classes.optionContainer}>
                <span className={classes.optionLabel}>
                  Use an existing board
                </span>
              </div>
            }
            className={classes.radioOption}
          />

          {selectedOption === 'existing' && (
            <div className={classes.inputContainer}>
              {isLoadingBoards ? (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <CircularProgress size={20} />
                  <span className={classes.loadingText}>Loading boards...</span>
                </div>
              ) : existingBoards.length === 0 ? (
                <p className={classes.noBoards}>
                  No existing boards found. Create a new one to get started.
                </p>
              ) : (
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedBoardId || ''}
                    onChange={e => handleBoardSelect(e.target.value)}
                    displayEmpty
                    variant="outlined"
                    className={classes.selectBox}
                    open={isDropdownOpen}
                    onOpen={() => setIsDropdownOpen(true)}
                    onClose={() => setIsDropdownOpen(false)}
                    MenuProps={{
                      PaperProps: {
                        className: classes.selectMenu,
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select a board
                    </MenuItem>
                    {existingBoards.map(board => (
                      <MenuItem key={board.id} value={board.id}>
                        {board.name}
                      </MenuItem>
                    ))}
                    {totalPages > 1 && (
                      <div
                        className={classes.paginationContainer}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                      >
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          disabled={isLoadingBoards}
                          size="small"
                        />
                      </div>
                    )}
                  </Select>
                </FormControl>
              )}
            </div>
          )}
        </RadioGroup>

        {error && (
          <div className={classes.errorContainer}>
            <p className={classes.errorText}>{error}</p>
          </div>
        )}

        <div className={classes.buttonContainer}>
          <CoreButton
            variant="outlined"
            size="small"
            onClick={onCancel}
            className={classes.cancelButton}
            disabled={isLoading}
          >
            Cancel
          </CoreButton>
          <CoreButton
            color="primary"
            size="small"
            onClick={handleCreate}
            disabled={isCreateDisabled || isLoading}
          >
            {isLoading ? (
              <>
                <CircularProgress size={16} style={{ marginRight: '8px' }} />
                {selectedOption === 'create' ? 'Creating...' : 'Selecting...'}
              </>
            ) : selectedOption === 'create' ? (
              'Create'
            ) : (
              'Select'
            )}
          </CoreButton>
        </div>
      </div>
    </Grid>
  )
}

export default withStyles(styles, { withTheme: true })(CommunityBoardModal)
